import React from 'react';
import { observer } from 'mobx-react';
import { useAuthStore, useLabattGameStore } from '../../contexts/mobx';
import { Card, Skeleton, Row, Col } from 'antd';
import { useSearchParams } from '../../utils/use-search-params';
import moment from 'moment';
import style from './style.module.scss';
import { useNavigation } from '../../utils/use-navigation';
import { RadioChangeEvent } from 'antd/lib/radio';
import { PageTopBar } from '../../components/page-top-bar';
import { GameDatesPicker } from '../../components/game-dates-picker';
import CalendarIcon from '../../icons/CalendarBlank.svg?react';
import ArrowsCounterClockwiseIcon from '../../icons/ArrowsCounterClockwise.svg?react';
import UsersIcon from '../../icons/Users.svg?react';
import ArrowLineRightIcon from '../../icons/ArrowLineRight.svg?react';
import TicketIcon from '../../icons/Ticket.svg?react';
import CheckCircleIcon from '../../icons/CheckCircle.svg?react';
import LabattStatCard from './LabattStatCard';
import { GamePerformanceTable } from './components/GamePerformanceTable';
import { TierDataTable } from './components/TierDataTable';
import { PlayCountDistributionChart } from './components/PlayCountDistributionChart';
import TrophyIcon from '../../icons/Trophy.svg?react';
import ChartLineIcon from '../../icons/ChartLine.svg?react';
import {
    ShoppingOutlined,
    StarOutlined,
    FireOutlined,
} from '@ant-design/icons';
import { TrendArrow } from '../../components/trend-arrow';
import {
    handleDatePickerChange,
    isValidDateRange,
} from '../../utils/date-picker-helpers';

interface MetricValue {
    current: number | null | undefined;
    previous: number | null | undefined;
}

const calculateDifference = ({ current, previous }: MetricValue): number => {
    if (typeof current === 'number' && typeof previous === 'number') {
        return ((current - previous) / previous) * 100;
    }
    return 0;
};

const MetricTrend: React.FC<{ value: number }> = ({ value }) => {
    return <TrendArrow value={value} size={32} showPercentage={true} />;
};

const GamePerformanceVisualization = observer(() => {
    const labattGameStore = useLabattGameStore();
    const analytics = labattGameStore.analytics;

    const renderMetricCard = (
        title: string,
        mainValue: number | null | undefined,
        mainLabel: string,
        mainTrend: MetricValue,
        metrics: {
            value: number | null | undefined;
            label: string;
            trend: MetricValue;
            icon: React.ReactNode;
        }[]
    ) => (
        <Card className={style.performance_card}>
            <h2>{title}</h2>
            <div className={style.metric_main}>
                <span className={style.metric_main_value}>
                    {mainValue ?? 0}
                </span>
                <span className={style.metric_main_label}>{mainLabel}</span>
                <span className={style.metric_trend}>
                    <MetricTrend value={calculateDifference(mainTrend)} />
                </span>
            </div>
            <div className={style.metric_details}>
                {metrics.map((metric, index) => (
                    <div key={index} className={style.metric_item}>
                        <span>
                            {metric.icon}
                            {metric.label}
                        </span>
                        <span className={style.metric_value}>
                            {metric.label === 'Redemption Rate'
                                ? `${metric.value?.toFixed(1)}%`
                                : metric.value ?? 0}
                            {metric.label !== 'Avg Games/User/Day' && (
                                <span className={style.metric_trend}>
                                    <MetricTrend
                                        value={calculateDifference(
                                            metric.trend
                                        )}
                                    />
                                </span>
                            )}
                        </span>
                    </div>
                ))}
            </div>
        </Card>
    );

    return (
        <div className={style.performance_visualization}>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={8}>
                    {renderMetricCard(
                        'Player Engagement',
                        analytics?.nbPlayers,
                        'Total Players',
                        {
                            current: analytics?.nbPlayers,
                            previous:
                                labattGameStore.previousAnalytics?.nbPlayers,
                        },
                        [
                            {
                                value: analytics?.nbNewUsers,
                                label: 'New Players',
                                trend: {
                                    current: analytics?.nbNewUsers,
                                    previous:
                                        labattGameStore.previousAnalytics
                                            ?.nbNewUsers,
                                },
                                icon: (
                                    <UsersIcon className={style.metric_icon} />
                                ),
                            },
                            {
                                value: analytics?.nbReturningPlayers,
                                label: 'Returning Players',
                                trend: {
                                    current: analytics?.nbReturningPlayers,
                                    previous:
                                        labattGameStore.previousAnalytics
                                            ?.nbReturningPlayers,
                                },
                                icon: (
                                    <ArrowLineRightIcon
                                        className={style.metric_icon}
                                    />
                                ),
                            },
                        ]
                    )}
                </Col>
                <Col xs={24} sm={24} md={8}>
                    {renderMetricCard(
                        'Game Activity',
                        analytics?.nbGamePlayed,
                        'Games Played',
                        {
                            current: analytics?.nbGamePlayed,
                            previous:
                                labattGameStore.previousAnalytics?.nbGamePlayed,
                        },
                        [
                            {
                                value: Math.round(
                                    analytics?.avgSessionTime || 0
                                ),
                                label: 'Avg Session Time',
                                trend: {
                                    current: analytics?.avgSessionTime,
                                    previous:
                                        labattGameStore.previousAnalytics
                                            ?.avgSessionTime,
                                },
                                icon: (
                                    <CalendarIcon
                                        className={style.metric_icon}
                                    />
                                ),
                            },
                            {
                                value: Number(
                                    (
                                        analytics?.avgSessionsPerPlayer ?? 0
                                    ).toFixed(1)
                                ),
                                label: 'Daily Replays',
                                trend: {
                                    current: analytics?.avgSessionsPerPlayer,
                                    previous:
                                        labattGameStore.previousAnalytics
                                            ?.avgSessionsPerPlayer,
                                },
                                icon: (
                                    <ArrowsCounterClockwiseIcon
                                        className={style.metric_icon}
                                    />
                                ),
                            },
                            {
                                value: Number(
                                    (
                                        labattGameStore.gameMetrics?.global
                                            .avgGamesPerUserPerDay ?? 0
                                    ).toFixed(2)
                                ),
                                label: 'Avg Games/User/Day',
                                trend: {
                                    current:
                                        labattGameStore.gameMetrics?.global
                                            .avgGamesPerUserPerDay,
                                    previous: null,
                                },
                                icon: (
                                    <ChartLineIcon
                                        className={style.metric_icon}
                                    />
                                ),
                            },
                        ]
                    )}
                </Col>
                <Col xs={24} sm={24} md={8}>
                    {renderMetricCard(
                        'Reward Distribution',
                        analytics?.couponsRedeemed,
                        'Redeemed',
                        {
                            current: analytics?.couponsRedeemed,
                            previous:
                                labattGameStore.previousAnalytics
                                    ?.couponsRedeemed,
                        },
                        [
                            {
                                value: analytics?.couponsWon,
                                label: 'Coupons Won',
                                trend: {
                                    current: analytics?.couponsWon,
                                    previous:
                                        labattGameStore.previousAnalytics
                                            ?.couponsWon,
                                },
                                icon: (
                                    <TicketIcon className={style.metric_icon} />
                                ),
                            },
                            {
                                value: Number(
                                    (
                                        ((analytics?.couponsRedeemed || 0) /
                                            (analytics?.couponsWon || 1)) *
                                        100
                                    ).toFixed(1)
                                ),
                                label: 'Redemption Rate',
                                trend: {
                                    current:
                                        (analytics?.couponsRedeemed || 0) /
                                        (analytics?.couponsWon || 1),
                                    previous:
                                        (labattGameStore.previousAnalytics
                                            ?.couponsRedeemed || 0) /
                                        (labattGameStore.previousAnalytics
                                            ?.couponsWon || 1),
                                },
                                icon: (
                                    <CheckCircleIcon
                                        className={style.metric_icon}
                                    />
                                ),
                            },
                        ]
                    )}
                </Col>
            </Row>
        </div>
    );
});

export const LabattGamePage = observer(() => {
    const authStore = useAuthStore();
    const labattGameStore = useLabattGameStore();
    const navigation = useNavigation();
    const [isLoading, setIsLoading] = React.useState(true);

    const { hasManagementAccess, isCustomerServiceAgent } = authStore;

    if (!hasManagementAccess && !isCustomerServiceAgent) {
        navigation.goToHome();
    }

    const search = useSearchParams<{
        startDate: string;
        endDate: string;
    }>();

    React.useEffect(() => {
        // Only make API calls if we have valid start and end dates
        if (isValidDateRange(search.params)) {
            setIsLoading(true);
            Promise.all([
                labattGameStore.getLabattGameAnalytics(search.params),
                labattGameStore.getPreviousPeriodAnalytics(search.params),
                labattGameStore.getGameMetrics(search.params),
            ]).finally(() => {
                setIsLoading(false);
            });
        }
    }, [search.params, labattGameStore]);

    const onChange = async (e: RadioChangeEvent) => {
        search.setParams({
            startDate: moment()
                .subtract(e.target.value, 'days')
                .startOf('day')
                .toISOString(),
            endDate: moment().subtract(1, 'days').endOf('day').toISOString(),
        });
    };

    const renderSkeleton = () => (
        <div>
            <div>
                <div>
                    <Skeleton active paragraph={{ rows: 4 }} />
                </div>
                <div>
                    <Skeleton active paragraph={{ rows: 4 }} />
                </div>
            </div>
        </div>
    );

    return (
        <>
            <PageTopBar title="Labatt Game Dashboard" />

            <div className={`${style.verticalContainer} content_with_margins`}>
                <div className={'date_picker_row'}>
                    <GameDatesPicker
                        onChange={onChange}
                        updateParams={(dates) => {
                            handleDatePickerChange(
                                dates,
                                (validDates) => {
                                    search.setParams({
                                        startDate: validDates[0]
                                            .startOf('day')
                                            .toISOString(),
                                        endDate: validDates[1]
                                            .endOf('day')
                                            .toISOString(),
                                    });
                                }
                                // No onClear callback - keep existing dates when cleared
                            );
                        }}
                        startDate={
                            search.params.startDate ||
                            moment()
                                .subtract(7, 'days')
                                .startOf('day')
                                .toISOString()
                        }
                        endDate={
                            search.params.endDate ||
                            moment()
                                .subtract(1, 'days')
                                .endOf('day')
                                .toISOString()
                        }
                    />
                </div>

                {isLoading ? (
                    <>
                        {renderSkeleton()}
                        <div>
                            <Skeleton active paragraph={{ rows: 4 }} />
                        </div>
                        <div>
                            <Skeleton active paragraph={{ rows: 4 }} />
                        </div>
                    </>
                ) : (
                    <>
                        <GamePerformanceVisualization />
                        {labattGameStore.gameMetrics && (
                            <GamePerformanceTable
                                games={labattGameStore.gameMetrics.perGame}
                            />
                        )}
                        {labattGameStore.gameMetrics?.tiers?.distribution && (
                            <TierDataTable
                                distribution={
                                    labattGameStore.gameMetrics.tiers
                                        .distribution
                                }
                                progression={
                                    labattGameStore.gameMetrics.tiers
                                        .progression
                                }
                                coupons={
                                    labattGameStore.gameMetrics.tiers.coupons
                                }
                            />
                        )}
                        {labattGameStore.gameMetrics?.perUser
                            ?.playCountDistribution &&
                            labattGameStore.gameMetrics.perUser
                                .playCountDistribution.length > 0 && (
                                <PlayCountDistributionChart
                                    playCountDistribution={
                                        labattGameStore.gameMetrics.perUser
                                            .playCountDistribution
                                    }
                                />
                            )}
                        <div>
                            <div className={style.table_containers}>
                                <h1>High Prize Stats</h1>
                                <div className={style.mini_card_container}>
                                    <LabattStatCard
                                        icon={
                                            <TrophyIcon
                                                className={style.icon_size}
                                            />
                                        }
                                        title="Paddle Board"
                                        stats={[
                                            {
                                                label: 'Won',
                                                value:
                                                    labattGameStore.analytics
                                                        ?.paddleWon ?? 0,
                                            },
                                            {
                                                label: 'Claimed',
                                                value:
                                                    labattGameStore.analytics
                                                        ?.paddleClaimed ?? 0,
                                            },
                                            {
                                                label: 'Rate',
                                                value: `${
                                                    labattGameStore.analytics
                                                        ?.paddleClaimed === 0
                                                        ? '0.0'
                                                        : '100.0'
                                                }%`,
                                            },
                                        ]}
                                    />
                                    <LabattStatCard
                                        icon={
                                            <ShoppingOutlined
                                                className={style.icon_font_size}
                                            />
                                        }
                                        title="Bag"
                                        stats={[
                                            {
                                                label: 'Won',
                                                value:
                                                    labattGameStore.analytics
                                                        ?.bagWon ?? 0,
                                            },
                                            {
                                                label: 'Claimed',
                                                value:
                                                    labattGameStore.analytics
                                                        ?.bagClaimed ?? 0,
                                            },
                                            {
                                                label: 'Rate',
                                                value: `${
                                                    labattGameStore.analytics?.bagRedemptionRate?.toFixed(
                                                        1
                                                    ) ?? '0.0'
                                                }%`,
                                            },
                                        ]}
                                    />
                                    <LabattStatCard
                                        icon={
                                            <StarOutlined
                                                className={style.icon_font_size}
                                            />
                                        }
                                        title="Ski"
                                        stats={[
                                            {
                                                label: 'Won',
                                                value:
                                                    labattGameStore.analytics
                                                        ?.skiWon ?? 0,
                                            },
                                            {
                                                label: 'Claimed',
                                                value:
                                                    labattGameStore.analytics
                                                        ?.skiClaimed ?? 0,
                                            },
                                            {
                                                label: 'Rate',
                                                value: `${
                                                    labattGameStore.analytics?.skiRedemptionRate?.toFixed(
                                                        1
                                                    ) ?? '0.0'
                                                }%`,
                                            },
                                        ]}
                                    />
                                    <LabattStatCard
                                        icon={
                                            <FireOutlined
                                                className={style.icon_font_size}
                                            />
                                        }
                                        title="Heater"
                                        stats={[
                                            {
                                                label: 'Won',
                                                value:
                                                    labattGameStore.analytics
                                                        ?.heaterWon ?? 0,
                                            },
                                            {
                                                label: 'Claimed',
                                                value:
                                                    labattGameStore.analytics
                                                        ?.heaterClaimed ?? 0,
                                            },
                                            {
                                                label: 'Rate',
                                                value: `${
                                                    labattGameStore.analytics?.heaterRedemptionRate?.toFixed(
                                                        1
                                                    ) ?? '0.0'
                                                }%`,
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                            <div>
                                <h1>Coupon Details</h1>
                                <div className={style.mini_card_container}>
                                    {(
                                        labattGameStore.analytics
                                            ?.couponDetails ?? []
                                    ).map((item) => (
                                        <LabattStatCard
                                            key={item.type}
                                            icon={
                                                <TicketIcon
                                                    className={style.icon_size}
                                                />
                                            }
                                            title={item.type}
                                            stats={[
                                                {
                                                    label: 'Won',
                                                    value: item.won,
                                                },
                                                {
                                                    label: 'Redeemed',
                                                    value: item.redeemed,
                                                },
                                                {
                                                    label: 'Rate',
                                                    value: `${item.redemptionRate.toFixed(
                                                        1
                                                    )}%`,
                                                },
                                            ]}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
});
